/**
 * The debrief (04 §2-§4): turns a `MissionSummary` into an `Aftermath` and moves the run on.
 * Order matters: captives are freed or lost before the flown ranger's own fate is applied,
 * readiness moves before the map is refreshed, and the ending is settled last.
 */

import { Pcg32 } from '../../sim/rng';
import type { Deployment } from '../api';
import { archetype, BIOME_NAMES } from '../content/archetypes';
import { hazardRules } from '../content/career';
import { COMMENDATIONS, RECOVERY } from '../content/economy';
import { TRAIT_RULES } from '../content/traits';
import { returnGear } from '../gear';
import { advanceTo, insertRescue, refreshNodes } from '../map';
import { recordFlight } from '../records';
import {
  activeOperation, companyRanger, findNode, type FullSummary, fullName, isAlive, type OperationState,
  type ProfileState, snapshot,
} from '../state';
import { markTagRecovery, settleTags } from '../tags';
import type { Aftermath, MedalId, MemorialEntry, RangerRecord } from '../types';
import { settleOperation } from './endings';
import { awardMedals } from './medals';
import { applyMerit } from './merit';
import { afterFlying, afterSittingOut, applyWounds, freeCaptives, loseCaptives, toMemorial } from './roster';
import { changeReadiness, readinessFor, rpEarned } from './tally';

/** Commendations for a mission's medals (04 §5). */
export function commendationsFor(medals: readonly MedalId[]): number {
  let total = 0;
  for (const m of medals) total += m === 'medal-of-honor' ? COMMENDATIONS.medalOfHonor : COMMENDATIONS.medal;
  return total;
}

/**
 * R6: Decorated says "alive in the company" and this asked `isAlive`, which counts a man missing
 * in action but not one in a prison camp. A prisoner is alive and still on the roster - and `mia`,
 * who is just as absent, always counted - so the exclusion was an accident of a shared predicate,
 * not a rule anyone wrote down. Dead, lost and retired men are off the company and cannot appear here.
 */
const inTheCompany = (r: RangerRecord): boolean => isAlive(r) || r.status === 'pow';

/** Exported so the trait's promise can be tested against the company directly (R6). */
export function decoratedBonus(op: OperationState, flown: RangerRecord): number {
  const inspired = op.company.some((r) => r.id !== flown.id && inTheCompany(r) && r.traits.includes('decorated'));
  return inspired ? 1 + TRAIT_RULES.decoratedMeritBonus : 1;
}

export function reportOperation(profile: ProfileState, deployment: Deployment, summary: FullSummary): Aftermath {
  const op = activeOperation(profile);
  const pending = op.deployed;
  if (!pending || pending.rangerId !== deployment.rangerId) {
    throw new Error('That deployment is not the one in the air.');
  }
  const node = findNode(op, pending.nodeId);
  const ranger = companyRanger(op, pending.rangerId);
  const params = pending.deployment.params;
  const rules = hazardRules(op.hazard);
  const rng = new Pcg32(params.seed, 0x0a17).fork(`${ranger.id}-${ranger.record.missions}`);
  const mission = `${archetype(params.archetype).name} (${BIOME_NAMES[params.biome]})`;
  op.deployed = null;

  const success = summary.outcome === 'extracted' && summary.objectiveComplete;
  const readiness = readinessFor(summary, success, node.keystone, rules);
  const readinessChange = changeReadiness(op, readiness.delta);
  const result: Aftermath = {
    ranger, meritGained: 0, rpGained: 0, promotedTo: null, perkChoices: null, medals: [], readinessChange,
    readinessReasons: readiness.reasons, freedRangers: [], memorial: null, operationStatus: op.status,
  };

  // Rare equipment goes back into the stock, or is lost with him (04 §6).
  const gearLost = returnGear(op, pending.deployment.loadout.gear, summary.outcome, summary.gearLeft);
  if (gearLost !== '') op.log.push(`Equipment lost: ${gearLost}.`);

  if (summary.outcome === 'scrubbed') {
    // Nobody jumped: no score, the node still stands (tag marker and all), the company does not age a mission.
    op.log.push(`${mission}: scrubbed. ${fullName(ranger)} never left the aircraft.`);
    refreshNodes(op);
    return finish(profile, op, result);
  }

  ranger.record.missions += 1;
  ranger.record.successes += success ? 1 : 0;
  ranger.record.kills += summary.kills;
  ranger.record.knifeKills += summary.knifeKills;
  const tags = settleTags(profile, op, node, summary);
  if (tags) result.tagRecovery = tags;
  if (summary.luckyUsed && !op.luckyUsed.includes(ranger.id)) op.luckyUsed.push(ranger.id);
  if (summary.preciseLanding) profile.landings[ranger.id] = (profile.landings[ranger.id] ?? 0) + 1;

  // Captives first, so a ranger taken on this very mission is not swept up with the old ones.
  if (summary.freedPows && params.archetype === 'pow-camp') result.freedRangers = freeCaptives(op);
  else if (node.kind === 'rescue') loseCaptives(profile, op, 'The camp was alerted and the prisoners were moved.');

  result.medals = awardMedals({
    ranger, summary, archetype: params.archetype, mode: 'operation', freed: result.freedRangers.length,
    landings: profile.landings[ranger.id] ?? 0,
  });
  profile.commendations += commendationsFor(result.medals);
  result.meritGained = Math.round(Math.max(0, summary.score) * decoratedBonus(op, ranger));
  // A Hall of Honor veteran has already won an operation, which is what COL asks for.
  result.promotedTo = applyMerit(ranger, result.meritGained, summary.outcome !== 'kia', ranger.veteran);

  // Filed here, not where the counters were raised: only now are his medals and his promotion known,
  // and those are the two things a service file is read for.
  recordFlight(profile, {
    archetype: params.archetype, rangerId: ranger.id, success, secondsUsed: summary.secondsUsed,
    entry: {
      operation: op.codename, mission, archetype: params.archetype, threat: summary.threat,
      outcome: summary.outcome, objectiveComplete: summary.objectiveComplete, score: summary.score,
      kills: summary.kills, wounds: summary.woundsAtEnd, medals: [...result.medals],
      ...(result.promotedTo === null ? {} : { promotedTo: result.promotedTo }),
    },
  });
  result.rpGained = rpEarned(node, summary, success);
  op.rp += result.rpGained;

  result.memorial = applyFate(profile, op, ranger, summary, mission, rng);
  afterSittingOut(op, ranger.id);
  resolveNode(profile, op, node.id, success, summary.score);
  op.log.push(`${mission}: ${success ? 'success' : 'failed'} - ${fullName(ranger)} ${FATE_TEXT[summary.outcome]}.`);
  // His tags lie where the company goes next in this theatre, once it is known where that is.
  if (summary.outcome === 'kia') markTagRecovery(op, ranger, node.theatre);
  return finish(profile, op, result);
}

const FATE_TEXT: Record<FullSummary['outcome'], string> = {
  extracted: 'extracted', kia: 'was killed', captured: 'was captured', walkedOut: 'is missing', scrubbed: 'stood down',
};

/** The flown ranger's own outcome (04 §3). */
function applyFate(
  profile: ProfileState, op: OperationState, ranger: RangerRecord, summary: FullSummary, mission: string, rng: Pcg32,
): MemorialEntry | null {
  if (summary.outcome === 'kia') {
    const cause = summary.cause ?? (summary.wasDowned ? 'Bled out before help could come' : 'Killed in action');
    return toMemorial(profile, op, ranger, 'kia', mission, cause, summary.photo);
  }
  if (summary.outcome === 'captured') {
    ranger.status = 'pow';
    ranger.record.timesCaptured += 1;
  } else if (summary.outcome === 'walkedOut') {
    ranger.status = 'mia';
    ranger.recoveryMissions = RECOVERY.mia;
  } else applyWounds(ranger, summary, hazardRules(op.hazard), rng);
  afterFlying(ranger, summary.outcome === 'extracted');
  return null;
}

/** Mark the node, count keystones, and move the company (a failed keystone is flown again from where it stands). */
function resolveNode(profile: ProfileState, op: OperationState, nodeId: string, success: boolean, score: number): void {
  const node = findNode(op, nodeId);
  const hasCaptives = op.company.some((r) => r.status === 'pow');
  if (success && node.params) {
    node.state = 'done';
    const { archetype: id, biome } = node.params;
    if (score > (profile.bestScores[id] ?? 0)) profile.bestScores[id] = score;
    if (!profile.firsts.includes(`${id}:${biome}`)) {
      profile.firsts.push(`${id}:${biome}`);
      profile.commendations += COMMENDATIONS.firstCompletion;
    }
    if (node.keystone) {
      op.keystoneFailures = 0;
      profile.commendations += COMMENDATIONS.keystone;
    }
  } else if (node.keystone) op.keystoneFailures += 1;
  else node.state = 'failed';

  if (success || !node.keystone) {
    const leftBehind = advanceTo(op, node, hasCaptives);
    if (leftBehind) loseCaptives(profile, op, 'The theatre was left behind with no rescue attempted.');
    return;
  }
  if (hasCaptives && op.rescueNodeId === null && op.position !== null) insertRescue(op, findNode(op, op.position));
  refreshNodes(op);
}

function finish(profile: ProfileState, op: OperationState, result: Aftermath): Aftermath {
  settleOperation(profile, op);
  const owed = result.ranger.status === 'kia' ? undefined : result.ranger.pendingPerkChoices[0];
  return snapshot({ ...result, perkChoices: owed ?? null, operationStatus: op.status });
}
